<script setup>
import { computed, toRefs, unref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import ActionButtonsCard from "@/components/shared/Card/ActionButtonsCard.vue";
import CareerInterestsCard from "@/components/shared/Card/CareerInterestsCard.vue";
import IndustriesCard from "@/components/shared/Card/IndustriesCard.vue";
import ProgramPriceCard from "@/components/shared/Card/ProgramPriceCard.vue";
import TitleCard from "@/components/shared/Card/TitleCard.vue";
import { hasDisplayStudentDiscountEnabled } from "@/composables/authorization";
import { hidePrice } from "@/util/pricingLabel";
import { handleClick, onMouseUp, useApplicationProgress } from "../../composables.js";
import {
  getCareerInterests,
  getIndustries,
  getInternshipCompensationTag,
  getIsPastAppDeadline,
  getPriceInDollars,
  resolvePricing,
} from "../utils";
import InternshipCardContent from "./InternshipCardContent.vue";
import { getInternshipRedirectUrl } from "./utils";

defineOptions({
  name: "InternshipCard",
});

const emit = defineEmits(["bookmarkClicked", "viewClicked", "applyClicked"]);

const store = useStore();
const router = useRouter();
const { getRegisterType, isReady } = useApplicationProgress();

const billingRules = computed(() => store.state.billingRules);
const homeInstitutionId = computed(() => store.state.profileData?.colleges?.[0]?.college_id ?? "");

const props = defineProps({
  hit: {
    type: Object,
    default() {
      return {};
    },
  },
  showViewLink: {
    type: Boolean,
    default: true,
  },
  horizontal: {
    type: Boolean,
    default: false,
  },
  randomTags: {
    type: Boolean,
    default: false,
  },
  showBookmark: {
    type: Boolean,
    default: false,
  },
  showImage: {
    type: Boolean,
    default: false,
  },
  uppercaseName: {
    type: Boolean,
    default: false,
  },
  eventCategory: {
    type: String,
    default: "",
  },
  imageUrl: {
    type: String,
    default: "",
  },
  domainEntityId: {
    type: String,
    required: true,
  },
});

const { hit, domainEntityId } = toRefs(props);

const session = computed(() => unref(hit)?.session);
const sessionId = computed(() => unref(session)?.id);

const programPage = computed(() => unref(hit)?.program_page);

const internship = computed(() => unref(hit)?.internship);

const showPriceComponent = computed(() => !hidePrice(unref(session), billingRules.value));

const priceInDollars = computed(() => getPriceInDollars(unref(hit), unref(homeInstitutionId)));

const pricing = computed(() => resolvePricing(session.value?.prices, homeInstitutionId.value, domainEntityId.value));
const originalCost = computed(() => pricing.value.original);
const actualCost = computed(() => pricing.value.actual);

const discountTitle = computed(() => session.value?.titles?.[domainEntityId.value]);

// Matches `ProgramPriceCard`'s own `showDiscount`. The title is an optional label, not a gate
// (DATSC-889 decoupled the two); this card kept the old gate until DATSC-1411.
const showDiscount = computed(() => !!originalCost.value && originalCost.value - actualCost.value > 0);
const redirectUrl = computed(() => getInternshipRedirectUrl(unref(hit)));
const isPastAppDeadline = computed(() => getIsPastAppDeadline(unref(hit)));
const title = computed(() => getInternshipCompensationTag(unref(internship), unref(session)));

const industries = computed(() => getIndustries(unref(internship)));
const careerInterests = computed(() => getCareerInterests(unref(internship)));
</script>

<template>
  <div
    id="internship-card"
    class="program-card-base h-full flex flex-col justify-between cursor-pointer"
    @mouseup="onMouseUp"
    @click="handleClick(emit)"
  >
    <div class="flex flex-col">
      <TitleCard
        :title="title"
        :action-id="sessionId"
        :show-bookmark="showBookmark"
        @bookmark-clicked="(id) => $emit('bookmarkClicked', id)"
      />
    </div>
    <div class="px-4 flex flex-col h-full">
      <InternshipCardContent
        v-if="!horizontal || (horizontal && showImage)"
        :internship="internship"
        :session="session"
        :program-page="programPage"
        :horizontal="horizontal"
        :random-tags="randomTags"
        :uppercase-name="uppercaseName"
        :perks="internship?.perks"
        :company-size="internship?.company?.company_size"
        :placement-types="internship?.placement_types"
      />
      <div class="flex flex-col flex-grow gap-y-11">
        <div id="api-card-description" class="grid grid-rows-[auto_1fr] flex-grow">
          <div class="w-full">
            <ProgramPriceCard
              v-if="showPriceComponent && showDiscount && hasDisplayStudentDiscountEnabled"
              :actual-cost="actualCost"
              :original-cost="originalCost"
              :discount-title="discountTitle"
            />
            <div
              v-else-if="priceInDollars && showPriceComponent"
              data-testid="program-price"
              class="font-bold uppercase text-base mt-2 mb-4 text-university-secondary"
            >
              Prices starting at {{ priceInDollars }}
            </div>
          </div>
          <div class="flex flex-col justify-end">
            <IndustriesCard v-if="industries" :industries="industries" />
            <CareerInterestsCard
              v-if="careerInterests"
              :career-interests="careerInterests"
              :session-id="sessionId"
            />
          </div>
        </div>
        <div
          class="flex flex-col sm:flex-row sm:justify-around justify-center items-center self-end w-full gap-4 mb-6"
        >
          <ActionButtonsCard
            v-if="isReady"
            :show-view-link="showViewLink"
            :redirect-url="redirectUrl"
            :program="programPage"
            custom-apply-class="program-card-apply-button-base"
            :is-past-app-deadline="isPastAppDeadline"
            :event-category="eventCategory"
            :register-type="getRegisterType(session)"
            @apply-clicked="(program) => $emit('applyClicked', program)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
